import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import { useEvidenceNumbers } from '../../../hooks/use-evidence-numbers'
import type { EvidenceNumber } from '../../../types/evidence-numbers'

interface EvidenceItemSelectorProps {
  caseId: string
  selectedIds: string[]
  onChange: (ids: string[]) => void
  filterDestroyed?: boolean
}

interface SelectorRowProps {
  item: EvidenceNumber
  checked: boolean
  onToggle: (id: string) => void
}

function SelectorRow({ item, checked, onToggle }: SelectorRowProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <label className="flex cursor-pointer items-center gap-3 rounded px-3 py-2 hover:bg-[var(--bg-elevated)]">
      <input
        type="checkbox"
        checked={checked}
        onChange={() => {
          onToggle(item.id)
        }}
        className="h-4 w-4 accent-[var(--accent)]"
        aria-label={item.evidenceNumber}
      />
      <span className="flex-1 text-sm font-[var(--font-mono)] text-[var(--text-primary)]">
        {item.evidenceNumber}
      </span>
      <span className="text-xs text-[var(--text-muted)]">
        {item.endpointHostname ?? item.endpointName ?? '--'}
      </span>
      {item.isDestroyed && (
        <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700 dark:bg-red-950/50 dark:text-red-400">
          {t('status.destroyed')}
        </span>
      )}
    </label>
  )
}

interface SelectorControlsProps {
  filter: string
  onFilterChange: (value: string) => void
  onSelectAll: () => void
  onDeselectAll: () => void
}

function SelectorControls({
  filter,
  onFilterChange,
  onSelectAll,
  onDeselectAll,
}: SelectorControlsProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]" />
        <input
          type="text"
          placeholder={t('custodyActions.filterEvidence')}
          value={filter}
          onChange={(ev) => {
            onFilterChange(ev.target.value)
          }}
          className="w-full rounded-lg border border-[var(--border)] bg-[var(--bg)] py-2 pr-3 pl-9 text-sm"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onSelectAll}
          className="text-xs text-[var(--accent)] hover:underline"
        >
          {t('custodyActions.selectAll')}
        </button>
        <span className="text-[var(--text-muted)]">/</span>
        <button
          type="button"
          onClick={onDeselectAll}
          className="text-xs text-[var(--accent)] hover:underline"
        >
          {t('custodyActions.deselectAll')}
        </button>
      </div>
    </div>
  )
}

interface FilterItemsOptions {
  textFilter: string
  filterDestroyed: boolean
}

function filterItems(items: EvidenceNumber[], options: FilterItemsOptions): EvidenceNumber[] {
  return items.filter((item) => {
    if (options.filterDestroyed && item.isDestroyed) return false
    if (options.textFilter.length === 0) return true
    const search = options.textFilter.toLowerCase()
    return (
      item.evidenceNumber.toLowerCase().includes(search) ||
      (item.endpointHostname ?? '').toLowerCase().includes(search) ||
      (item.endpointName ?? '').toLowerCase().includes(search)
    )
  })
}

export function EvidenceItemSelector({
  caseId,
  selectedIds,
  onChange,
  filterDestroyed = true,
}: EvidenceItemSelectorProps): React.ReactElement {
  const { t } = useTranslation('evidence')
  const [textFilter, setTextFilter] = useState('')
  const { data, loading, error } = useEvidenceNumbers(caseId)

  const visible = filterItems(data, { textFilter, filterDestroyed })

  const handleToggle = useCallback(
    (id: string) => {
      if (selectedIds.includes(id)) {
        onChange(selectedIds.filter((sid) => sid !== id))
      } else {
        onChange([...selectedIds, id])
      }
    },
    [selectedIds, onChange]
  )

  const handleSelectAll = useCallback(() => {
    onChange(visible.map((item) => item.id))
  }, [visible, onChange])
  const handleDeselectAll = useCallback(() => {
    onChange([])
  }, [onChange])

  if (loading)
    return (
      <div role="status" aria-busy="true" className="py-4 text-sm text-[var(--text-muted)]">
        {t('loading')}
      </div>
    )
  if (error)
    return (
      <div role="alert" className="py-4 text-sm text-red-600 dark:text-red-400">
        {error}
      </div>
    )

  return (
    <div className="flex flex-col gap-3">
      <SelectorControls
        filter={textFilter}
        onFilterChange={setTextFilter}
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
      />
      <div className="max-h-48 overflow-y-auto rounded-lg border border-[var(--border)] bg-[var(--bg-card)]">
        {visible.length === 0 ? (
          <p className="px-3 py-4 text-sm text-[var(--text-muted)]">
            {t('custodyActions.noEvidenceItems')}
          </p>
        ) : (
          visible.map((item) => (
            <SelectorRow
              key={item.id}
              item={item}
              checked={selectedIds.includes(item.id)}
              onToggle={handleToggle}
            />
          ))
        )}
      </div>
    </div>
  )
}
